import { inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';

import { BehaviorSubject, Observable, catchError, filter, map, switchMap, take, throwError } from 'rxjs';

import { MsalAuthService } from '../msal/msal-auth.service';
import { ErrorCode } from '@models/enums/error.codes';
import { HttpError } from '@models/classes/http-error.model';
import { Environment } from '@environment/environment';
import { NetworkService } from '@services/network/network.service';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly authSvc: MsalAuthService = inject(MsalAuthService);
    private readonly networkSvc: NetworkService = inject(NetworkService);
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    constructor() {}
    // **/public/** */

    private byPassUrl(url: string): boolean {
        if (url.startsWith(`${Environment.baseUrl}public`) || url.startsWith(Environment.attachmentBaseUrl)) {
            return true;
        } else {
            return false;
        }
    }

    private addToken(request: HttpRequest<any>, token: string | null) {
        return request.clone({
            setHeaders: {
                authorization: 'Bearer ' + token,
            },
        });
    }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this.networkSvc.isOnline) {
            if (!request.url.endsWith('i18/en.json')) {
                return throwError(() => new HttpError('No internet connection', ErrorCode.NO_INTERNET, 0, request.url));
            }
        }

        // Bypass certain URLs

        //  if (request.url.includes('/public')) {
        //   return next.handle(request);
        // }
        if (this.byPassUrl(request.url)) {
            return next.handle(request);
        }

        // Proceed with existing token
        const token = this.authSvc.getAccessToken();
        const clone = this.addToken(request, token);

        return next.handle(clone).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse && !event.ok) {
                    const errorMessage = event.body?.message || event.body?.msg || 'An error occurred';
                    throw new HttpError(errorMessage, ErrorCode.SomethingWentWrong, event.status, request.url);
                }
                return event;
            }),
            catchError((error: HttpErrorResponse) => {
                // Handle specific error cases
                if (error.status === 401) {
                    if (error.error?.code === 'TOKEN_EXPIRED') {
                        this.authSvc.logoutRedirect();
                    } else {
                        return this.handle401Error(request, next);
                    }
                }

                if (error.status === 403) {
                    return throwError(() => new HttpError('Access forbidden', ErrorCode.Forbidden, error.status, request.url));
                }

                if (error.status === 404) {
                    return throwError(() => new HttpError('Resource not found', ErrorCode.NotFound, error.status, request.url));
                }

                if (error.status === 500) {
                    return throwError(() => new HttpError('Internal server error', ErrorCode.InternalServerError, error.status, request.url));
                }

                // Handle error codes from backend
                if (error?.error?.code === ErrorCode.ContactAdmin) {
                    return throwError(() => new HttpError('Contact admin', ErrorCode.ContactAdmin, error.status, request.url));
                }

                if (error.status === 0 || error.status === -1) {
                    if (!this.networkSvc.isOnline) {
                        return throwError(() => new HttpError('No internet connection', ErrorCode.NO_INTERNET, error.status, request.url));
                    } else {
                        return throwError(() => new HttpError('Network error', ErrorCode.NETWORK_ERROR, error.status, request.url));
                    }
                }

                // Create a meaningful error message from the error response
                const errorMessage = error.error?.message || error.error?.msg || error.message || 'An unexpected error occurred';

                return throwError(() => new HttpError(errorMessage, error.error?.code || ErrorCode.SomethingWentWrong, error.status, request.url));
            })
        );
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.authSvc.refreshToken().pipe(
                switchMap((user: any) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(user.accessToken);
                    return next.handle(this.addToken(request, user.accessToken));
                }),
                catchError((err) => {
                    this.isRefreshing = false;
                    throw err;
                })
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter((token) => token != null),
                take(1),
                switchMap((accessToken) => {
                    return next.handle(this.addToken(request, accessToken));
                })
            );
        }
    }
}
